import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { FaCheckCircle, FaEye, FaSearch, FaArrowLeft, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';

function ClientCompletedJobs({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState({});
  const [clientId, setClientId] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchClientData(userData);
  }, []);

  const fetchClientData = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/clients?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.clients && data.clients.length > 0) {
        const id = data.clients[0].id;
        setClientId(id);
        fetchJobs(id);
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
    } finally {
      // Never leave the page spinning (e.g. if no client record is returned).
      setLoading(false);
    }
  };

  const fetchJobs = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`/jobs/client/${id}`);
      const data = await response.json();
      if (data.success) {
        const completed = data.jobs.filter(j => j.status === 'completed');
        setJobs(completed);
        setFilteredJobs(completed);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = jobs;
    if (searchTerm) {
      filtered = filtered.filter(j => 
        j.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.commodity?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredJobs(filtered);
  }, [searchTerm, jobs]);

  if (loading) {
    return (
      <ClientLayout activeTab="completed" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading history...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout activeTab="completed" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            ✅ Completed Jobs
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {filteredJobs.length} completed jobs
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search completed jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>
        </div>
      </div>

      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold mb-2" style={{ color: '#0A1929' }}>No Completed Jobs</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>You haven't completed any jobs yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <div key={job.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold" style={{ color: '#0A1929' }}>{job.job_number}</h3>
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-600">✅ Completed</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm" style={{ color: '#4A5568' }}>
                    <span className="flex items-center gap-1">{job.commodity}</span>
                    <span className="flex items-center gap-1">{job.total_quantity_mt} MT</span>
                    <span className="flex items-center gap-1">{job.origin} → {job.destination}</span>
                    <span className="flex items-center gap-1">R{job.client_rate || 0}/MT</span>
                    <span className="flex items-center gap-1"><FaCalendarAlt size={12} /> {job.start_date}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:border-green hover:text-green transition-colors" style={{ borderColor: '#E8ECEF' }}>
                    <FaEye size={14} /> View
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </ClientLayout>
  );
}

export default ClientCompletedJobs;
